import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Layers,
  Plus,
  AlertCircle,
  MapPin,
  Satellite,
  CheckCircle2,
  X,
  ArrowLeft,
  Navigation,
  Camera,
  XCircle,
} from 'lucide-react'
import { getTrainingSamples, createTrainingSample, linkSpectralData, getPaddocks, uploadFieldPhoto } from '../../services/api'
import { format, parseISO } from 'date-fns'
import StatCard from '../../components/StatCard'
import { useAuth } from '../../contexts/AuthContext'

const VEGETATION_TYPES = {
  native_grass:     { label: 'Native Grass',      color: 'bg-green-100 text-green-700' },
  improved_pasture: { label: 'Improved Pasture',   color: 'bg-emerald-100 text-emerald-700' },
  lantana:          { label: 'Lantana',            color: 'bg-red-100 text-red-700' },
  woody_invasive:   { label: 'Woody Invasive',    color: 'bg-orange-100 text-orange-700' },
  savannah_tree:    { label: 'Savannah Tree',      color: 'bg-amber-100 text-amber-700' },
  bare_soil:        { label: 'Bare Soil',          color: 'bg-gray-200 text-gray-700' },
  water:            { label: 'Water',              color: 'bg-blue-100 text-blue-700' },
  other:            { label: 'Other',              color: 'bg-purple-100 text-purple-700' },
}

const CONFIDENCE_LABELS = {
  field_verified: 'Field Verified',
  estimated:      'Estimated',
  uncertain:      'Uncertain',
}

export default function TrainingData({ ranchId }) {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [paddockFilter, setPaddockFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [linkingId, setLinkingId] = useState(null)
  const [linkError, setLinkError] = useState('')

  const { data: samplesData, isLoading } = useQuery({
    queryKey: ['trainingSamples', ranchId, paddockFilter, typeFilter],
    queryFn: () => getTrainingSamples(ranchId, {
      ...(paddockFilter && { paddock_id: paddockFilter }),
      ...(typeFilter && { vegetation_type: typeFilter }),
    }),
  })

  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  const linkMutation = useMutation({
    mutationFn: (sampleId) => {
      setLinkingId(sampleId)
      return linkSpectralData(sampleId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingSamples'] })
      setLinkingId(null)
      setLinkError('')
    },
    onError: (err) => {
      setLinkError(err.response?.data?.detail || 'No satellite data within 7 days')
      setLinkingId(null)
    },
  })

  // Auto-clear link error after 5s
  useEffect(() => {
    if (linkError) {
      const t = setTimeout(() => setLinkError(''), 5000)
      return () => clearTimeout(t)
    }
  }, [linkError])

  const samples = samplesData?.items || []
  const totalSamples = samplesData?.total || 0
  const typesRepresented = new Set(samples.map(s => s.vegetation_type)).size
  const withSpectral = samples.filter(s => s.satellite_date).length

  // Paddock ID → paddock object lookup
  const paddockMap = {}
  paddocks?.forEach(p => { paddockMap[p.id] = p })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-col sm:flex-row gap-4">
        <div className="flex items-center gap-3">
          <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Training Data</h1>
            <p className="text-gray-500 mt-1">Field vegetation observations for land cover mapping</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Sample
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Samples"
          value={totalSamples}
          icon={Layers}
          color="green"
        />
        <StatCard
          title="Veg Types Covered"
          value={typesRepresented}
          subtitle="of 8 types"
          icon={MapPin}
          color="blue"
        />
        <StatCard
          title="Satellite Verified"
          value={withSpectral}
          subtitle={totalSamples > 0 ? `${Math.round((withSpectral / totalSamples) * 100)}% of samples` : null}
          icon={Satellite}
          color="purple"
        />
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Paddock:</label>
            <select
              value={paddockFilter}
              onChange={e => setPaddockFilter(e.target.value)}
              className="input max-w-xs"
            >
              <option value="">All paddocks</option>
              {paddocks?.map(p => (
                <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Type:</label>
            <select
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}
              className="input max-w-xs"
            >
              <option value="">All types</option>
              {Object.entries(VEGETATION_TYPES).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Link error banner */}
      {linkError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {linkError}
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : !samples.length ? (
          <div className="text-center py-12">
            <Layers className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No training samples yet</h3>
            <p className="text-gray-500 mt-1">Record field observations to improve land cover mapping</p>
            <button
              onClick={() => setShowModal(true)}
              className="btn bg-green-600 text-white hover:bg-green-700 mt-4 inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Sample
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paddock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vegetation Type</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Cover %</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Height</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confidence</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Satellite</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {samples.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(parseISO(s.observation_date), 'dd MMM yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                        {paddockMap[s.paddock_id]?.code || '...'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${VEGETATION_TYPES[s.vegetation_type]?.color || 'bg-gray-100 text-gray-600'}`}>
                        {VEGETATION_TYPES[s.vegetation_type]?.label || s.vegetation_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {s.cover_pct != null ? `${s.cover_pct}%` : '\u2014'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {s.grass_height_cm != null ? `${s.grass_height_cm} cm` : '\u2014'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {CONFIDENCE_LABELS[s.confidence] || s.confidence}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {s.satellite_date ? (
                        <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          {format(parseISO(s.satellite_date), 'dd MMM')}
                        </span>
                      ) : (
                        <button
                          onClick={() => linkMutation.mutate(s.id)}
                          disabled={linkingId === s.id}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                        >
                          {linkingId === s.id ? 'Linking...' : 'Link Satellite'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showModal && (
        <CreateSampleModal
          ranchId={ranchId}
          paddocks={paddocks || []}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['trainingSamples'] })
            setShowModal(false)
          }}
        />
      )}
    </div>
  )
}

function CreateSampleModal({ ranchId, paddocks, onClose, onSuccess }) {
  const { userId } = useAuth()
  const today = new Date().toISOString().split('T')[0]
  const [formData, setFormData] = useState({
    paddock_id: '',
    latitude: '',
    longitude: '',
    observation_date: today,
    vegetation_type: '',
    cover_pct: '',
    grass_height_cm: '',
    dominant_species: '',
    confidence: 'field_verified',
    notes: '',
  })
  const [error, setError] = useState('')
  const [gpsLoading, setGpsLoading] = useState(false)
  const [photoUrl, setPhotoUrl] = useState(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const fileInputRef = useRef(null)

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingPhoto(true)
    try {
      const result = await uploadFieldPhoto(file, 'training')
      setPhotoUrl(result.url)
    } catch {
      setError('Failed to upload photo')
    } finally {
      setUploadingPhoto(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const mutation = useMutation({
    mutationFn: createTrainingSample,
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to save sample'),
  })

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported by your browser')
      return
    }
    setGpsLoading(true)
    setError('')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFormData(prev => ({
          ...prev,
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        }))
        setGpsLoading(false)
      },
      (err) => {
        setError(`GPS error: ${err.message}`)
        setGpsLoading(false)
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!formData.paddock_id || !formData.vegetation_type) {
      setError('Select a paddock and vegetation type')
      return
    }
    if (!formData.latitude || !formData.longitude) {
      setError('Enter coordinates or use GPS location')
      return
    }

    mutation.mutate({
      ranch_id: ranchId,
      paddock_id: formData.paddock_id,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude),
      observation_date: formData.observation_date,
      vegetation_type: formData.vegetation_type,
      cover_pct: formData.cover_pct ? parseFloat(formData.cover_pct) : null,
      grass_height_cm: formData.grass_height_cm ? parseFloat(formData.grass_height_cm) : null,
      dominant_species: formData.dominant_species || null,
      confidence: formData.confidence,
      notes: formData.notes || null,
      photo_url: photoUrl,
      collected_by: userId,
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Record Vegetation Sample</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Paddock + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Paddock *</label>
              <select
                value={formData.paddock_id}
                onChange={e => handleChange('paddock_id', e.target.value)}
                className="input w-full"
                required
              >
                <option value="">Select paddock</option>
                {paddocks.map(p => (
                  <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Date *</label>
              <input
                type="date"
                value={formData.observation_date}
                onChange={e => handleChange('observation_date', e.target.value)}
                className="input w-full"
                required
              />
            </div>
          </div>

          {/* GPS Coordinates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Latitude *</label>
              <input
                type="number"
                value={formData.latitude}
                onChange={e => handleChange('latitude', e.target.value)}
                placeholder="e.g. 0.3475"
                className="input w-full"
                step="0.000001"
                required
              />
            </div>
            <div>
              <label className="label">Longitude *</label>
              <input
                type="number"
                value={formData.longitude}
                onChange={e => handleChange('longitude', e.target.value)}
                placeholder="e.g. 32.584"
                className="input w-full"
                step="0.000001"
                required
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleUseLocation}
            disabled={gpsLoading}
            className="btn w-full bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 flex items-center justify-center gap-2"
          >
            {gpsLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                Getting location...
              </>
            ) : (
              <>
                <Navigation className="h-4 w-4" />
                Use My Location
              </>
            )}
          </button>

          {/* Vegetation Type + Confidence */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Vegetation Type *</label>
              <select
                value={formData.vegetation_type}
                onChange={e => handleChange('vegetation_type', e.target.value)}
                className="input w-full"
                required
              >
                <option value="">Select type</option>
                {Object.entries(VEGETATION_TYPES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Confidence</label>
              <select
                value={formData.confidence}
                onChange={e => handleChange('confidence', e.target.value)}
                className="input w-full"
              >
                {Object.entries(CONFIDENCE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Cover + Height */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Cover %</label>
              <input
                type="number"
                value={formData.cover_pct}
                onChange={e => handleChange('cover_pct', e.target.value)}
                placeholder="e.g. 85"
                className="input w-full"
                min="0"
                max="100"
                step="1"
              />
            </div>
            <div>
              <label className="label">Grass Height (cm)</label>
              <input
                type="number"
                value={formData.grass_height_cm}
                onChange={e => handleChange('grass_height_cm', e.target.value)}
                placeholder="e.g. 45"
                className="input w-full"
                min="0"
                step="0.5"
              />
            </div>
          </div>

          {/* Dominant Species */}
          <div>
            <label className="label">Dominant Species</label>
            <input
              type="text"
              value={formData.dominant_species}
              onChange={e => handleChange('dominant_species', e.target.value)}
              placeholder="e.g. Hyparrhenia rufa, Brachiaria"
              className="input w-full"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => handleChange('notes', e.target.value)}
              placeholder="Any observations about this site..."
              className="input w-full"
              rows={2}
            />
          </div>

          {/* Photo Upload */}
          <div>
            <label className="label">Photo</label>
            {photoUrl && (
              <div className="relative inline-block mb-2">
                <img src={photoUrl} alt="Sample" className="h-20 w-20 object-cover rounded-lg border" />
                <button
                  type="button"
                  onClick={() => setPhotoUrl(null)}
                  className="absolute -top-1.5 -right-1.5"
                >
                  <XCircle className="h-4 w-4 text-red-500 bg-white rounded-full" />
                </button>
              </div>
            )}
            {!photoUrl && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="btn bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100 text-sm flex items-center gap-2"
              >
                {uploadingPhoto ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-600"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Camera className="h-3.5 w-3.5" />
                    Take Photo
                  </>
                )}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              onChange={handlePhotoSelect}
              className="hidden"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Layers className="h-4 w-4" />
                  Save Sample
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
